import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { AriaAlertContext } from '../../AriaAlertContext';
import EyeOffSvg from '../../assets/icons/eye-off.svg';
import EyeSvg from '../../assets/icons/eye.svg';
import LockSvg from '../../assets/icons/secured-lock.svg';
import { colors } from '../constants';
import {
  DIGITS_INCLUDE_X_REGEX,
  DIGITS_REGEX,
  NON_DIGITS_EXCEPT_X_REGEX,
} from '../utilities/formatting';
import { cloneField, getFieldClone } from '../utilities/syntheticEventTracking';

import { Button } from './Button';
import {
  ErrorMessageBubble,
  ErrorMessageBubbleIcon,
  ErrorMessageText,
  MessageBubble,
} from './MessageBubble';
import { StyledInputProps } from './TextInput';

// SsnTextInput

// Extends base component TextInput for SSN inputs
// Looks like a textinput with type=password, but with nonmasked hyphens to template the input
// Masks the digits of the input value with Unicode 'BLACK CIRCLE' - ●
// Displays the formatted input value with the following template: ●●●-●●-●●●●
// Returns unformatted and unmasked string of digits to form handlers

// How it works:
//   - There are two inputs in this component, one visible and one hidden
//   - The hidden input is the real input whose value gets passed to the form
//   - The visible input is a proxy for the user to interact with the input value
//   - Event handlers for the proxy input make any necessary changes to the events before
//     passing them off to the handlers of the real input, e.g. removing template format

// Additional Differences from TextInput:
//   - No longer accepted as props: type, pattern, autoComplete, maxLength, max, min
//   - Does not allow inputs to be copied, pasted, or selected/highlighted
//   - Contains a visibility toggle to show/hide the unmasked input value on click

// Interfaces -------------------------------------------------------------------------------------
export interface SSNInputProps {
  name: string;
  value: string;
  setFieldTouched?: (e?: React.FocusEvent<any>) => void;
  label?: string | undefined;
  disabled?: boolean;
  error?: string | undefined;
  touched?: boolean | undefined;
  showInfoOnFocus?: boolean;
  showSecuredCopy?: boolean;
  onFocus?: (() => void) | (() => undefined);
  onBlur?: (e: React.FocusEvent<any>) => void;
  onChange: ((e: React.ChangeEvent<any>) => void) | (() => undefined);
  required?: boolean;
  isFullSsn?: boolean; // True for full SSN, false for just the last four digits
}

// Subcomponents ----------------------------------------------------------------------------------
export const SsnInputInfoCopy = `Your SSN is used to make sure we identify your credit
  history and not someone else’s. All information entered is kept safe and secure.`;

// MessageBubble with informational copy shown while input is focused
const FocusedMessageBubble = (name: string) => (
  <MessageBubble
    id={`${name}-hints-bubble`}
    data-testid={`${name}-message-bubble`}
  >
    <p>{SsnInputInfoCopy}</p>
  </MessageBubble>
);

// #region Styles ---------------------------------------------------------------------------------
// Outer container spans width of parent
const Container = styled.div`
  width: 100%;
`;
Container.displayName = 'Container';

// Inner container includes label, input, and validation elements
// Relatively positioned to ensure children are properly positioned
const Content = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;
Content.displayName = 'Content';

// Resembles a placeholder for an unfocused Input with an empty input value
// For inputs with focus or non-empty values, the label shrinks and slides up above the input value
// Label changes text color to red when an error exists for a touched input
// Props:
// slide - moves label to make room for user input, i.e. onFocus, or for non-empty value
// showError - changes text color to red
const Label = styled.label<StyledInputProps>`
  position: absolute;
  left: 1rem;
  display: flex;
  justify-content: center;
  line-height: 3.5rem;
  font-size: 1rem;
  color: ${colors.TEXT_LABEL};
  pointer-events: none;
  transition: transform 0.175s ease-out 0s;
  transform-origin: left top;

  ${(p: StyledInputProps) =>
    p.slide &&
    `
    transform: translateY(-12%) scale(0.75);
  `};
`;
Label.displayName = 'Label';

// Input changes border color to red when an error exists for a touched input
// Upon hover/focus, border is emphasized with color change and box shadow
// When disabled, the hover behavior is no longer applied
// Props:
// showError - changes border color to red
const Input = styled.input<StyledInputProps>`
  height: 3.5rem;
  width: 100%;
  min-height: 3.5rem;
  border-radius: 0.125rem;
  border: 0.0625rem solid ${colors.BORDER_PRIMARY};
  padding: 1.75rem 2.1875rem 1rem 0.875rem;
  font-size: 1rem;
  color: ${colors.TEXT_INPUT};
  outline: none;
  appearance: none;

  ${(p: StyledInputProps) =>
    p.showError &&
    `
    border-color: ${colors.TEXT_ERROR};
  `};

  /* Hover/Focus - change border color to outline color and adds box shadow */
  /* When an error exists, the border color and box shadow turn red */
  &:hover,
  &:focus {
    box-shadow: 0 0 0.25rem ${colors.UI_OUTLINE}26;
    border-color: ${colors.UI_OUTLINE};

    ${(p: StyledInputProps) =>
      p.showError &&
      `
      box-shadow: 0 0 0.25rem ${colors.TEXT_ERROR}26;
      border-color: ${colors.TEXT_ERROR};
    `};
  }

  /* Disabled - remove hovering border color change and box shadow */
  &:disabled {
    box-shadow: none;
    border-color: ${colors.BORDER_PRIMARY};
    cursor: no-drop;
    background-color: rgba(239, 239, 239, 0.3);
  }
`;
Input.displayName = 'Input';

// Position toggle on the inner right edge of the input box
const InsetContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 3.5rem;
  display: flex;
  align-items: center;
`;
InsetContainer.displayName = 'InsetContainer';

// Inset button with eye icons representing each toggle state
const InsetToggle = styled(Button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: none;
  padding: 0;
  height: 1.5rem;
  width: 1.5rem;

  /* Show border in focus-visible state, adjust position to account for offset */
  &:focus-visible {
    top: 0.875rem;
    right: 0.875rem;
    border: 0.0625rem solid ${colors.TEXT_PRIMARY};
    border-radius: 0.25rem;
    padding: 0.0625rem 0.0625rem;
    height: 1.75rem;
    width: 1.75rem;
  }
`;
InsetToggle.displayName = 'InsetToggle';

const EyeIcon = styled(EyeSvg)``;
EyeIcon.displayName = 'EyeIcon';

const EyeOffIcon = styled(EyeOffSvg)``;
EyeOffIcon.displayName = 'EyeOffIcon';

// Hidden real input stores the actual value
const RealInput = styled.input`
  display: none;
`;
RealInput.displayName = 'RealInput';

const MaskedLabel = styled.label`
  display: none;
`;
MaskedLabel.displayName = 'MaskedLabel';

// Hidden error message is revealed when an error exists for a touched input
// Props:
// isVisible - toggles display between flex and none
const ErrorMessage = styled(ErrorMessageBubble)<StyledInputProps>`
  display: none;
  ${(p: StyledInputProps) =>
    p.isVisible &&
    `
    display: flex;
  `};
`;
ErrorMessage.displayName = 'ErrorMessage';

const SecuredContainer = styled.div`
  display: inline-flex;
  vertical-align: bottom;
  margin: 0.75rem 0;
  line-height: 1.5rem;
`;
SecuredContainer.displayName = 'SecuredContainerr';

const SecuredMessage = styled.p`
  padding: 0 0 0 0.25rem;
  color: ${colors.TEXT_LABEL};
`;
SecuredMessage.displayName = 'SecuredMessage';

const LockIcon = styled(LockSvg)`
  height: 1.5rem;
`;
LockIcon.displayName = 'LockIcon';
// #endregion

// Component --------------------------------------------------------------------------------------
export const SsnTextInput: FC<SSNInputProps> = ({
  name, // required, used for ID and for proper form functionality
  value, // required
  label,
  disabled,
  error, // error message string, undefined when input passes validation
  touched,
  showInfoOnFocus, // optionally suppress MessageBubble while input is focused
  showSecuredCopy, // optionally display encryption info under unfocused input
  onFocus,
  onBlur,
  onChange, // validation callback for current input value
  setFieldTouched,
  required,
  isFullSsn,
}) => {
  // Constants ------------------------------------------------------------------------------------
  const MASK_CHAR = '•';
  const HYPHEN_CHAR = '-';
  const SECURED_COPY = 'Secured by 256-bit encryption.';

  // State ----------------------------------------------------------------------------------------
  // Display the formatted, un-/masked input value
  const [displayValue, setDisplayValue] = useState(value);
  // Show or hide the real input value on toggle
  const [showInput, setShowInput] = useState(false);
  // Transform label when input is focused or has a nonempty value
  const [adjustLabel, setAdjustLabel] = useState(false);
  // Show the hint message below the input
  const [showHint, setShowHint] = useState(false);
  // Track value length for masking logic
  const [lastValueLength, setLastValueLength] = useState(0);

  // Hooks ----------------------------------------------------------------------------------------
  const { setAriaAlertMessage } = useContext(AriaAlertContext);

  const inputRef = useRef(null);

  // Functions ------------------------------------------------------------------------------------
  // Remove SSN formatting
  const removeFormat = (formatted: string) => {
    const unformatted = (formatted || '').replace(
      NON_DIGITS_EXCEPT_X_REGEX,
      ''
    );
    return unformatted;
  };

  // Add SSN format with template •••-••-••••
  const applyFormat = (ssn: string) => {
    // Remove existing format before reformatting
    const rawSsn = removeFormat(ssn);
    if (isFullSsn) {
      // Add two '-' when length of raw ssn value is greater than five characters
      if (rawSsn.length > 5) {
        return `${rawSsn.slice(0, 3)}-${rawSsn.slice(3, 5)}-${rawSsn.slice(5)}`;
      }

      // Add one '-' when length of raw ssn value is between three and five characters
      if (rawSsn.length > 3 && rawSsn.length <= 5) {
        return `${rawSsn.slice(0, 3)}-${rawSsn.slice(3)}`;
      }

      // No formatting is required when length of raw ssn value is less than three characters
      return value;
    }
    return rawSsn;
  };

  // Replace each digit of the input value with the mask char
  // showLast can be set to show the last digit of the ssn without masking
  const mask = (formattedVal: any, showLast = false) => {
    const maskedValue = showLast
      ? formattedVal
          .slice(0, -1)
          .replace(DIGITS_INCLUDE_X_REGEX, MASK_CHAR)
          .concat(formattedVal.slice(-1))
      : formattedVal.replace(DIGITS_INCLUDE_X_REGEX, MASK_CHAR);
    return maskedValue;
  };

  const replaceCharacter = (current: string, previous: string): string => {
    let unmasked = '';
    // Construct string using previous value except indexes in current value with digit chars
    for (let i = 0; i < current.length; i += 1) {
      const currentChar = current.charAt(i);
      unmasked +=
        currentChar !== HYPHEN_CHAR && currentChar !== MASK_CHAR
          ? currentChar
          : previous.charAt(i);
    }
    return unmasked;
  };
  const addCharacter = (
    current: string,
    previous: string,
    cursorIndex: number
  ): string => {
    const beforeChanges = previous.slice(0, cursorIndex - 1);
    const changes = current.slice(cursorIndex - 1, cursorIndex);
    const afterChanges = previous.slice(cursorIndex - 1);
    return `${beforeChanges}${changes}${afterChanges}`;
  };

  const removeCharacter = (previous: string, cursorIndex: number): string => {
    let removalIndex = cursorIndex;

    if (isFullSsn) {
      // Adjust the removal index if it's next to a hyphen in a full SSN format
      if (cursorIndex === 3 || cursorIndex === 6) {
        removalIndex -= 1;
      }
    }
    const before = previous.slice(0, removalIndex);
    const after = previous.slice(cursorIndex + 1);
    return `${before}${after}`;
  };

  // Construct the real change to the value using the changes to the display input
  // e.g. props.value = 123456, current = '•••-••-•7', previous = 123-45-6, unmasked = 123-45-67
  const unmask = (current: string, cursorIndex: number): string => {
    const previous = applyFormat(value);
    const lengthDifference = current.length - previous.length;

    if (lengthDifference === 0) return replaceCharacter(current, previous);
    if (lengthDifference === 1) {
      return addCharacter(current, previous, cursorIndex);
    }
    if (lengthDifference === -1) return removeCharacter(previous, cursorIndex);
    if (lengthDifference > 0) return '';
    if (lengthDifference < 0) return '';
    return '';
  };

  // The event handlers onFocus, onBlur, onKeyPress, onChange are identical to those in TextInput
  // onFocus - controls label animation and calls focus handler from props
  const handleFocus = () => {
    // always show the hint
    setShowHint(true);
    // Move aside the label when input is focused
    setAdjustLabel(true);
    if (onFocus) onFocus();
  };

  // onKeyPress - restrict value updates to allow digits only
  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Ensure the new character is allowed by the numbers regexp pattern
    if (!e.key.match(DIGITS_REGEX)) {
      // Do not allow input if new character does not match pattern
      e.preventDefault();
    }
  };

  // Change the name of e.target to the real input before handling keypress event
  const handleKeyPressMask = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Change the name and ID of e.target to those of the real input
    (e.target as HTMLInputElement).id = name;
    (e.target as HTMLInputElement).name = name;
    handleKeyPress(e);
  };

  // Pass event e to the real input change handler after unmasking and setting e.target.value
  const handleChangeMask = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Change the name and ID of e.target to those of the real input
    e.target.id = name;
    e.target.name = name;

    // Get the current value and cursor index of the proxy input
    const currentValue = e.target.value;
    const cursorIndex = e.target.selectionStart || 0;

    // Unmask and strip format from value before passing it to the change handler of real input
    const unmasked = unmask(currentValue, cursorIndex);
    const unmaskedUnformatted = removeFormat(unmasked);

    // Use the updated, unmasked, unformatted value in the base change handler of the real input
    e.target.value = unmaskedUnformatted;
    onChange(e);
  };

  // Do not allow selection/highlight of characters in the input value
  const handleSelectMask = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Set cursor to max allowed length (the end of the field)
    e.target.setSelectionRange(11, 11);
  };

  // Re-trigger the input blur handler to run formik.validate on the latest input value
  const handleBlurToggle = (e: React.FocusEvent<HTMLButtonElement>) => {
    const input = inputRef.current;
    if (input && (!e.relatedTarget || e.relatedTarget !== input)) {
      setFieldTouched(e);
      // Stop showing the hint when the input is blurred
      setShowHint(false);
    }
  };

  // Catch-all workaround to inconsistent browser event tracking
  const trackChangeOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Clone the blur event and dispatch it as a change on a corresponding input element clone
    try {
      // Cast the blur event clone as a change event
      const syntheticEvent = new Event('change', e.nativeEvent);
      // Get the ID and form of the blurred input field
      const { id, form } = e.target;
      // Get the corresponding element clone if one exists, otherwise create one
      let clone = getFieldClone(id);
      const firstBlurWithChange = !clone && value !== '';
      if (!clone) clone = cloneField(id, document.getElementById(id), form);
      // Dispatch the synthetic event to simulate the change when the clone/real values differ
      const cloneValue = clone.getAttribute('value');
      if (cloneValue !== value || firstBlurWithChange) {
        clone.dispatchEvent(syntheticEvent);
        clone.setAttribute('value', value);
      }
    } catch {
      // TO-DO error handler - probably not worth a NPC-Client-Error
    }
  };

  // onBlur - changes state to indicate input is touched and calls blur handler from props
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Stop showing the hint when the input is blurred
    setShowHint(false);
    // Reset label to initial position/scale if input value is still empty
    if (value === undefined || (value !== undefined && value.length === 0)) {
      setAdjustLabel(false);
    }
    // Trigger synthetic change for Heap tracking purposes
    trackChangeOnBlur(e);
    // Call blur handler from props with the entire event object
    if (onBlur) onBlur(e);
  };

  // Extended functionality - process events here before passing them to handlers of real input
  // Change the name of e.target to the real input before handling blur event
  const handleBlurMask = (e: React.FocusEvent<HTMLInputElement>) => {
    // Change the name and ID of e.target to those of the real input
    e.target.id = name;
    e.target.name = name;
    if (error && setAriaAlertMessage) {
      setAriaAlertMessage(error);
    }
    handleBlur(e);
  };

  // Format updated input value and set displayValue accordingly
  useEffect(() => {
    let timer: NodeJS.Timeout;
    const formattedValue = applyFormat(value);
    if (showInput) {
      // Display unmasked ssn value if showInput is true
      const unmaskedValue = unmask(formattedValue, value.length - 1);
      setDisplayValue(unmaskedValue);
    } else {
      let maskedValue = mask(formattedValue);
      // Display delayed masking when entering new numbers
      if (value.length > lastValueLength) {
        maskedValue = mask(formattedValue, true);
        setDisplayValue(maskedValue);
        // Delay masking last digit
        timer = setTimeout(() => {
          maskedValue = mask(formattedValue);
          setDisplayValue(maskedValue);
        }, 700);
      } else {
        // Display full masking as usual
        setDisplayValue(maskedValue);
      }
    }
    // Slide up and shrink the input label if input value is nonempty
    if (value !== undefined && value.length > 0) {
      setAdjustLabel(true);
    }
    setLastValueLength(value.length);
    return () => clearTimeout(timer);
  }, [value, showInput]);

  // Render ---------------------------------------------------------------------------------------
  return (
    <Container>
      <Content>
        <Label
          htmlFor={name}
          slide={adjustLabel}
          showError={!!error && touched}
        >
          {label}
        </Label>
        <RealInput
          id={name}
          name={name}
          value={value}
          type="tel"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={onChange}
          onCopy={() => false}
          onPaste={() => false}
          aria-required={required}
          data-testid="ssn-input"
        />
        <Input
          id={`masked-${name}`}
          name={`masked-${name}`}
          value={displayValue}
          type="tel"
          maxLength={isFullSsn ? 11 : 4}
          disabled={disabled}
          autoComplete="off"
          showError={!!error && touched}
          onFocus={handleFocus}
          onBlur={handleBlurMask}
          onKeyPress={handleKeyPressMask}
          onChange={handleChangeMask}
          onSelect={handleSelectMask}
          onCopy={() => false}
          onPaste={() => false}
          data-testid={`${name}-masked-input`}
          ref={inputRef}
          aria-label={label}
          aria-required={required}
          aria-describedby={`${name}-error ${name}-secured-message-copy ${name}-hints-bubble`}
          aria-invalid={error && touched}
        />
        <MaskedLabel htmlFor={`masked-${name}`}>{label}</MaskedLabel>
        {showSecuredCopy && !((error && touched) || showHint) && (
          <SecuredContainer>
            <LockIcon aria-hidden />
            <SecuredMessage
              id={`${name}-secured-message-copy`}
              data-testid={`${name}-secured-message-copy`}
            >
              {SECURED_COPY}
            </SecuredMessage>
          </SecuredContainer>
        )}
        <ErrorMessage isVisible={!!error && touched && !showHint}>
          <ErrorMessageText id={`${name}-error`}>{error}</ErrorMessageText>
          <ErrorMessageBubbleIcon />
        </ErrorMessage>
        {showHint && showInfoOnFocus && FocusedMessageBubble(name)}
        {/* remove InsetToogle when disable is true */}
        {adjustLabel && !disabled && (
          <InsetContainer>
            <InsetToggle
              id="toggle"
              type="button"
              aria-label={`Show ${label}`}
              aria-pressed={showInput}
              onClick={() => setShowInput(!showInput)}
              onBlur={handleBlurToggle}
              tabIndex={displayValue ? 0 : -1}
            >
              {!showInput && <EyeIcon role="img" aria-label="EyeIcon" />}
              {showInput && <EyeOffIcon role="img" aria-label="EyeOffIcon" />}
            </InsetToggle>
          </InsetContainer>
        )}
      </Content>
    </Container>
  );
};

SsnTextInput.defaultProps = {
  label: undefined,
  disabled: false,
  error: undefined,
  touched: undefined,
  showInfoOnFocus: false,
  showSecuredCopy: false,
  onFocus: () => {},
  onBlur: () => {},
  setFieldTouched: () => {},
  required: true,
  isFullSsn: true,
};
